<template>
  <div ref="container" class="image-annotation">
    <annotation-tool-bar
      v-show="showImageAnnotationToolbar"
      ref="annotationToolBar"
      :button-config-list="getToolbarConfig(type)"
      :annotatable="true"
      @color-and-shape-selected="onColorAndShapeSelected"
    />
  </div>
</template>

<script setup lang="ts">
import DOMUtils from "@utils/dom.utils";
import {
  ANNOTATION_MODE,
  ANNOTATION_TYPE,
  IMAGE_ANNOTATION_RADIUS,
} from "@modules/annotation/service/annotation.constant";
import {
  buildMediaObjectAnchor,
  parseOwningComponentIdFromAnchor,
} from "@modules/annotation/service/annotation.anchor.utils.ts";
import { debounceFn } from "@/utils/debounce";
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import AnnotationToolBar from "./AnnotationToolBar.vue";
import { AnnotationEventBus } from "@modules/annotation/service/annotation.event.bus";
import { useDynamicMountChild } from "@plugins/common/composables/dynamic-mount-child.ts";
import { useAnnotation } from "@modules/annotation/composables/annotation.ts";
import { useAnnotationNote } from "@modules/annotation/composables/annotation.note.ts";
import { useAnnotationAttemptAdapter } from "@modules/annotation/composables/use-annotation-attempt-adapter.ts";
import { usePageDrawer } from "@modules/app/composables/page.drawer.ts";
import { useStore } from "vuex";
import { Annotation, AnnotationContext } from "@modules/annotation/types.ts";

interface HighlightDataInput {
  class: string;
  top: number;
  left: number;
  displayTop: number;
  displayLeft: number;
}

interface Props {
  anchor?: string;
  annotationResourceType?: string;
  index?: string;
  annotationResourceId?: string;
  ownerType?: string;
  question?: unknown[];
  getMentionableTeacherList?: () => Record<string, unknown>;
  getMentionableStudentList?: () => Record<string, unknown>;
  viewingAnnotation?: object;
  // eslint-disable-next-line vue/prop-name-casing
  dynamicMountChildMixin_parentComponent?: object;
  // eslint-disable-next-line vue/prop-name-casing
  dynamicMountChildMixin_isDynamicallyMounted?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  anchor: "",
  annotationResourceType: "",
  index: "",
  annotationResourceId: "",
  ownerType: "USER",
  question: () => [],
  getMentionableTeacherList: () => ({}),
  getMentionableStudentList: () => ({}),
  viewingAnnotation: () => ({}),
  dynamicMountChildMixin_parentComponent: () => ({}),
  dynamicMountChildMixin_isDynamicallyMounted: false,
});

useDynamicMountChild({
  dynamicMountChildMixin_parentComponent: props.dynamicMountChildMixin_parentComponent,
  dynamicMountChildMixin_isDynamicallyMounted: props.dynamicMountChildMixin_isDynamicallyMounted,
});

const currentAnchor = computed(() => {
  return buildMediaObjectAnchor(props.anchor, props.index);
});

const {
  isShowHighlightToolbar,
  hideHighlightToolbar,
  showHighlightToolbar,
  setWithNote,
  getColorShapeSelector,
  createDraftAnnotation,
  setSelectedAnnotation,
  setLastCreatedImageAnnotation,
  getToolbarConfig,
  setActiveImageAnnotation,
} = useAnnotation(currentAnchor.value);

const annotationToolBarRef =
  useTemplateRef<InstanceType<typeof AnnotationToolBar>>("annotationToolBar");

const annotationContext = inject<AnnotationContext>("annotationContext");

const container = useTemplateRef("container");
const mode = ref(ANNOTATION_MODE.VIEW);
const annotatable = ref(false);
const newAnnotationX = ref(0);
const newAnnotationY = ref(0);
const bgImageScale = ref(1);
const clickEvent = ref(null);
const imgZoomOrReset = ref(false);
const mediaTitle = ref<string | null>(null);
const unwatch = ref<(() => void) | null>(null);
const unwatchRepositionImage = ref<(() => void) | null>(null);

const store = useStore();

const { handleCollapseDrawer, isPageDrawerExpanded } = usePageDrawer({
  drawerName: annotationContext?.drawerName ?? "",
});

const attemptAdapter = useAnnotationAttemptAdapter();
const lastCreatedImageAnnotation = computed<Record<string, Annotation>>(
  () => store.state.annotation.lastCreatedImageAnnotation,
);
const activeImageAnnotation = computed<Record<string, Annotation>>(
  () => store.state.annotation.activeImageAnnotation,
);

const showImageAnnotationToolbar = computed(() => {
  return isShowHighlightToolbar.value && isAnnotatable.value && !isImgZoomOrReset.value;
});

const isAnnotatable = computed(() => {
  return annotatable.value;
});

const isImgZoomOrReset = computed(() => {
  return imgZoomOrReset.value;
});

const isView = computed(() => {
  return mode.value === ANNOTATION_MODE.VIEW;
});

const isEdit = computed(() => {
  return mode.value === ANNOTATION_MODE.EDIT;
});

const resourceId = computed(() => {
  if (props.annotationResourceId !== "") {
    return parseInt(props.annotationResourceId);
  }

  const contextComponentId = Number(annotationContext?.componentId);
  return Number.isFinite(contextComponentId) && contextComponentId > 0
    ? contextComponentId
    : parseOwningComponentIdFromAnchor(props.anchor);
});

const hasSelectedImageAnnotation = computed(() => {
  return (
    selectedAnnotation.value.annotation &&
    selectedAnnotation.value.annotation.type === ANNOTATION_TYPE.IMAGE_ANNOTATION &&
    selectedAnnotation.value.annotation.anchor === `${props.anchor}_${props.index}`
  );
});

const isLastCreatedAnnotation = computed(() => {
  return (
    lastCreatedImageAnnotation.value !== null &&
    lastCreatedImageAnnotation.value.annotation.id === selectedAnnotation.value.annotation.id
  );
});

const type = computed(() => {
  return ANNOTATION_TYPE.IMAGE_ANNOTATION;
});

const selectedAnnotation = computed(() => {
  return store.getters["annotation/getSelectedAnnotation"];
});

const bindResize = () => {
  window.onresize = debounceFn(() => repositionAnnotation(), 500);
};

const handleClick = (params) => {
  imgZoomOrReset.value = false;
  const idx = params.event.target.getAttribute("index");

  if (annotatable.value && idx === props.index) {
    if (!isEdit.value) {
      clickEvent.value = params.event;
      newAnnotationX.value = params.coordinate.x;
      newAnnotationY.value = params.coordinate.y;
      bgImageScale.value = params.scale;
      mediaTitle.value = params.mediaTitle;

      showHighlightToolbar();
      annotationToolBarRef.value.handleAnnotationSelected(
        clickEvent.value?.target,
        getColorbarPopperConfig(clickEvent.value),
      );
    }
  }
};

const getColorbarPopperConfig = (clickEvent) => {
  const hasClickEvent = clickEvent !== null;

  return hasClickEvent
    ? {
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [
                newAnnotationX.value * bgImageScale.value -
                  (getImageInfo().width * bgImageScale.value) / 2,
                newAnnotationY.value * bgImageScale.value -
                  getImageInfo().height * bgImageScale.value +
                  IMAGE_ANNOTATION_RADIUS * 2,
              ],
            },
          },
        ],
      }
    : {};
};

const handleImgZoomOrReset = () => {
  // handle image zoomed or reset after double clicking
  imgZoomOrReset.value = true;
};

const isModeChanged = (params) => {
  if (props.index === params.index) {
    // Hide existing toolbar and editor to reset workflow
    hideHighlightToolbar();
    setSelectedAnnotation({
      annotation: null,
      mode: ANNOTATION_MODE.VIEW,
    });

    // Set annotatable based on note toggle
    annotatable.value = params.isAnnotatable;
    if (annotatable.value) {
      setActiveImageAnnotation(`${props.anchor}_${props.index}`);
    }
  }
};

const repositionAnnotation = () => {
  if (isView.value) {
    clearAnnotation();
  } else if (isEdit.value && hasSelectedImageAnnotation.value) {
    clearAnnotation();
  }
};

const handleRepositionImage = (params: { anchor: string; index: string }): void => {
  if (params.anchor === props.anchor && params.index === props.index) {
    repositionAnnotation();
  }
};

const getMediaUuid = () => {
  const mediaElement = container.value?.closest(".media");
  return mediaElement instanceof HTMLElement ? (mediaElement.dataset.uuid ?? "") : "";
};

const createImageAnnotation = (selector: string) => {
  const highlightData = createHighlightData(newAnnotationX.value, newAnnotationY.value, selector);
  const position = getImagePosition();
  const ownerId = annotationContext?.userId ?? attemptAdapter.currentUserId.value;

  const requestData = {
    resourceUuid: annotationContext.resourceUuid,
    resourceType: annotationContext.resourceType,
    annotationResourceId: resourceId.value,
    annotationResourceType: props.annotationResourceType,
    anchor: `${props.anchor}_${props.index}`,
    contextType: annotationContext?.contextType,
    type: ANNOTATION_TYPE.IMAGE_ANNOTATION,
    data: highlightData,
    title: mediaTitle.value,
    owner: annotationContext.userId,
    collectionOwner: annotationContext.userId,
    groupingId: annotationContext.pageId,
    allowComment: true,
    ownerType: props.ownerType,
    ownerId,
    indexPosition: position,
    visibleByStudents: false,
    mediaUuid: getMediaUuid(),
  };

  createDraftAnnotation(requestData);
  handleEditNotes(ANNOTATION_TYPE.IMAGE_ANNOTATION, props.question);
  clearNewAnnotationData();
  // Set image annotatable to false, user needs to enable annotation again
  annotatable.value = false;
};

const createHighlightData = (clientX: string, clientY: string, selector: string) => {
  const imgInfo = getImageInfo();
  const top = parseFloat(clientY) / imgInfo.height;
  const left = parseFloat(clientX) / imgInfo.width;
  const annotationRadius = IMAGE_ANNOTATION_RADIUS / 2;

  const data = {
    class: selector,
    top: top,
    left: left,
    displayTop: parseFloat(clientY) - annotationRadius,
    displayLeft: parseFloat(clientX) - annotationRadius,
  };
  const highlightData = populateHighlightData(data);
  return highlightData;
};

const populateHighlightData = (data: HighlightDataInput) => {
  const id = Date.now();
  return {
    highlighterId: id,
    highlighterClass: data.class,
    highlightedContent: "",
    highlighterSelectors: [
      {
        type: "DATA_POSITION_SELECTOR",
        top: data.top,
        left: data.left,
      },
    ],
  };
};

const createAnnotationContainer = () => {
  // Create div for annotation points
  const containerEl = container.value;
  const parent = DOMUtils.parent(containerEl, ".media-image-view");
  const imageWrapper = DOMUtils.elOf(parent, ".image-wrapper")[0];
  const div = document.createElement("div");
  div.setAttribute("class", "annotation-points");
  imageWrapper.appendChild(div);
};

const removeAnnotationContainer = () => {
  // Remove any existing image annotation container
  const containerEl = container.value;
  const parent = DOMUtils.parent(containerEl, ".media-image-view");
  const annotationDiv = DOMUtils.elOf(parent, ".annotation-points")[0];
  if (annotationDiv) {
    annotationDiv.remove();
  }
};

const clearAnnotation = () => {
  const containerEl = container.value;
  const parent = DOMUtils.parent(containerEl, ".media-image-view");
  if (parent) {
    const imageWrapper = DOMUtils.elOf(parent, ".image-wrapper")[0];
    DOMUtils.elOf(imageWrapper, ".annotation-points").forEach((element: Element) => {
      element.innerHTML = "";
    });
  }
};

const getImageInfo = () => {
  const richText = container.value?.closest(".annotatable-richtext");
  if (!richText) return null;
  const imgSelector = `.media.image [index='${props.index}']`;
  const imgElement = DOMUtils.elOf(richText, imgSelector)[0];
  return {
    width: imgElement.clientWidth,
    height: imgElement.clientHeight,
    top: imgElement.getBoundingClientRect().top,
    left: imgElement.getBoundingClientRect().left,
  };
};

const getImagePosition = () => {
  const richText = container.value?.closest(".annotatable-richtext");
  const imgSelector = `.media.image [index='${props.index}']`;
  let position;
  // Get position of image in richtext
  DOMUtils.elOf(richText, imgSelector).forEach((element: Element) => {
    const range = (window as unknown as { rangy: unknown }).rangy.createRange();
    range.selectNodeContents(element);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(richText);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    position = preCaretRange.toString().length;
  });
  return position;
};

const onColorAndShapeSelected = (clickEvent: MouseEvent) => {
  const classList = (clickEvent.currentTarget as Element).classList;
  setWithNote(true);
  createImageAnnotation(getColorShapeSelector(classList));
  hideHighlightToolbar();
};

const clearNewAnnotationData = () => {
  newAnnotationX.value = "";
  newAnnotationY.value = "";
};

const deactiveNoteToggle = () => {
  const params = {
    anchor: props.anchor,
    index: props.index,
  };
  AnnotationEventBus.$emit("annotationDeactiveNoteToggle", params);
};

const resetImageZoom = () => {
  const params = {
    anchor: props.anchor,
    index: props.index,
  };
  AnnotationEventBus.$emit("annotationResetZoom", params);
};

const getContext = () => {
  return new Promise((resolve, reject) => {
    if (selectedAnnotation.value && selectedAnnotation.value.annotation) {
      const selectedAnn = selectedAnnotation.value.annotation;
      resolve({
        resourceType: "ANNOTATION",
        resourceUuid: selectedAnn.uuid,
        resourceId: selectedAnn.id,
      });
      return;
    }

    // this case is not expected,
    // image annotation should have been created.
    reject();
  });
};

const { handleEditNotes } = useAnnotationNote({
  deactiveNoteToggle: undefined,
  handleNoteChangeConfirm: undefined,
  handleNoteChangeCancel: undefined,
  getContext: getContext,
  getMentionableTeacherList: props.getMentionableTeacherList,
  getMentionableStudentList: props.getMentionableStudentList,
  mode: "",
});

onMounted(() => {
  bindResize();
  removeAnnotationContainer();
  createAnnotationContainer();

  unwatchRepositionImage.value = AnnotationEventBus.$on(
    "annotationRepositionImage",
    handleRepositionImage,
  );

  unwatch.value = store.watch(
    () => store.getters["annotation/getSelectedAnnotation"],
    () => {
      if (selectedAnnotation.value !== undefined) {
        if (selectedAnnotation.value.mode === ANNOTATION_MODE.EDIT) {
          mode.value = ANNOTATION_MODE.EDIT;
        } else if (selectedAnnotation.value.mode === ANNOTATION_MODE.DELETE) {
          mode.value = ANNOTATION_MODE.DELETE;
        } else if (selectedAnnotation.value.mode === ANNOTATION_MODE.VIEW) {
          mode.value = ANNOTATION_MODE.VIEW;
        }
      }
    },
  );
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", repositionAnnotation);
  unwatchRepositionImage.value?.();
  if (unwatch.value) {
    unwatch.value();
  }
});

// Watchers
watch(showImageAnnotationToolbar, () => {
  const toolbar = DOMUtils.elOf(container.value, ".annotation-toolbar")[0] as HTMLElement;
  if (!toolbar) return;
  if (showImageAnnotationToolbar.value) {
    toolbar.style.display = "";
  } else {
    toolbar.style.display = "none";
  }
});

watch(mode, () => {
  if (isView.value) {
    // Render updated annotations
    clearAnnotation();
  } else if (isEdit.value) {
    // Load saved annotation
    clearAnnotation();
    if (hasSelectedImageAnnotation.value) {
      // Reset zoom during edit, to make sure that the edited annotation point is inside viewport
      if (!isLastCreatedAnnotation.value) {
        resetImageZoom();
      }
      nextTick(() => {
        nextTick(() => {
          handleCollapseDrawer().then(() => {
            handleEditNotes(ANNOTATION_TYPE.IMAGE_ANNOTATION, props.question);
            setLastCreatedImageAnnotation(null);
          });
        });
      });
    }
  }
});

watch(activeImageAnnotation, () => {
  if (activeImageAnnotation.value !== `${props.anchor}_${props.index}`) {
    // If image is not selected, turn off annotation, hide toolbar and editor
    annotatable.value = false;
    deactiveNoteToggle();
    hideHighlightToolbar();
    setSelectedAnnotation({
      annotation: null,
      mode: ANNOTATION_MODE.VIEW,
    });
  }
});

watch(isPageDrawerExpanded, () => {
  // Reposition annotation points when page drawer is expanded/collapsed
  setTimeout(() => {
    if (!isEdit.value) {
      repositionAnnotation();
    }
  }, 200);
});

defineExpose({
  handleClick,
  handleImgZoomOrReset,
  isModeChanged,
});
</script>
